import * as React from 'react';
import { Avatar, Box, Chip, Divider, Link, Skeleton, Stack, Typography } from '@mui/material';
import {
  Buildings as BuildingsIcon,
  CalendarBlank as CalendarBlankIcon,
  House as HouseIcon,
  MapPin as MapPinIcon,
  ShieldCheck as ShieldCheckIcon,
} from '@phosphor-icons/react';

import type { User } from '@/types/v2/user';
import type { Community } from '@/types/v2/community';
import { paths } from '@/paths';
import { dayjs } from '@/lib/v2/dayjs';
import { SectionLabel } from '@/pages/v2/requests/request/components/request-details-card';

function KVRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" sx={{ py: 1.25, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      <Typography
        color="text.secondary"
        sx={{ fontSize: '0.8125rem', flexShrink: 0, minWidth: 90 }}
        variant="body2"
      >
        {label}
      </Typography>
      <Typography component="div" sx={{ fontSize: '0.8125rem', fontWeight: 500, textAlign: 'right' }} variant="body2">
        {children}
      </Typography>
    </Stack>
  );
}

interface UserSidebarProps {
  resident?: User | null;
  community: Community;
  isLoading: boolean;
}

export function UserSidebar({ resident, community, isLoading }: UserSidebarProps): React.JSX.Element {
  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={18} variant="text" />
        ))}
      </Stack>
    );
  }

  const units = resident?.units ?? [];
  const isVerified = !!resident?.email_verified_at;
  const communityRole = resident?.community_roles?.[0]?.name;
  const roleName = communityRole
    ? communityRole.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Member';

  return (
    <Stack divider={<Divider />} spacing={0}>
      {/* Summary */}
      <Box sx={{ pb: 2 }}>
        <SectionLabel>Overview</SectionLabel>
        <Stack sx={{ mt: 1 }} divider={<Divider flexItem />}>
          <KVRow label="Community">
            {community?.name ?? '—'}
          </KVRow>
          <KVRow label="Role">
            <Chip
              label={roleName}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6875rem',
                fontWeight: 600,
                bgcolor: communityRole === 'community_manager' ? 'primary.main' : 'action.selected',
                color: communityRole === 'community_manager' ? 'primary.contrastText' : 'text.primary',
              }}
            />
          </KVRow>
          <KVRow label="Status">
            <Chip
              label={isVerified ? 'Verified' : 'Unverified'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6875rem',
                fontWeight: 600,
                bgcolor: isVerified ? 'success.main' : 'warning.main',
                color: '#fff',
              }}
            />
          </KVRow>
          {resident?.created_at && (
            <KVRow label="Joined">
              {dayjs(resident.created_at).format('MMM D, YYYY')}
            </KVRow>
          )}
        </Stack>
      </Box>

      {/* Units summary */}
      {units.length > 0 && (
        <Box sx={{ py: 2 }}>
          <SectionLabel icon={<HouseIcon size={14} />}>
            Units
            <Chip
              label={units.length}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', ml: 1 }}
              variant="outlined"
            />
          </SectionLabel>
          <Stack spacing={0.75} sx={{ mt: 1.5 }}>
            {units.map((unit) => (
              <Link
                href={paths.units.details(unit.uuid ?? String(unit.id))}
                key={unit.id}
                underline="none"
                sx={{ color: 'inherit' }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                    py: 0.75,
                    px: 1,
                    borderRadius: 1,
                    transition: 'background-color 0.12s',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'primary.main' }}>
                    {unit.unit_number ? `#${unit.unit_number}` : 'U'}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                      {unit.unit_number ? `Unit ${unit.unit_number}` : unit.address || 'Unit'}
                    </Typography>
                    {(unit.address || unit.city) && (
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                        <MapPinIcon size={10} />
                        <Typography noWrap sx={{ fontSize: '0.7rem' }}>
                          {[unit.address, unit.city].filter(Boolean).join(', ')}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Link>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
